import React, { useState, useEffect, useMemo } from 'react';
import {
  FaTimes,
  FaPlus,
  FaCheck,
  FaChevronDown,
  FaChevronRight,
  FaShieldAlt,
  FaMinus,
  FaGripVertical,
  FaSave
} from 'react-icons/fa';
import { CloseModal } from '../sites/closemodal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axiosInstance from '../../libs/axios';
import toast from 'react-hot-toast';

interface Permission {
  permission_id: number;
  name: string;
  description: string;
  resource: string;
}

interface RoleFormData {
  name: string;
  description: string;
  permissions: number[];
}

interface AddRoleOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RoleFormData) => void;
  permissions: Record<string, Permission[]>;
  loading?: boolean;
}

const formatDisplayText = (value?: string) => {
  if (!value) return "";
  return value
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
};

// Sortable Permission Item
const SortablePermissionItem: React.FC<{
  id: string;
  permission: Permission;
  isSelected: boolean;
  onToggle: () => void;
}> = ({ id, permission, isSelected, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-start gap-2 p-2.5 rounded border transition-all duration-200 ${
        isSelected
          ? 'border-gray-300 bg-gray-100 shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <FaGripVertical className="w-3 h-3 text-gray-400" />
      </div>
      <div className="relative flex-shrink-0 mt-0.5">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="w-4 h-4 rounded border-gray-300 text-gray-600 focus:ring-gray-500"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-900 group-hover:text-gray-700">
          {formatDisplayText(permission.name)}
        </div>
        {permission.description && (
          <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">
            {formatDisplayText(permission.description)}
          </div>
        )}
      </div>
    </div>
  );
};

// Sortable Group
const SortableGroup: React.FC<{
  id: string;
  children: React.ReactNode;
}> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'shadow-lg rounded' : ''}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            dragHandleProps: { ...attributes, ...listeners },
          });
        }
        return child;
      })}
    </div>
  );
};

export const AddRoleOffcanvas: React.FC<AddRoleOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  permissions,
  loading = false
}) => {
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissions: []
  });

  const [errors, setErrors] = useState<any>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [orderedGroups, setOrderedGroups] = useState<string[]>([]);
  const [orderedPermissions, setOrderedPermissions] = useState<Record<string, Permission[]>>({});
  const [savingOrder, setSavingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', description: '', permissions: [] });
      setErrors({});
      setShowCloseModal(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const groups = Object.keys(permissions || {})
    setOrderedGroups(groups)
    setOrderedPermissions({ ...(permissions || {}) })
  }, [permissions])

  const requestClose = () => {
    setShowCloseModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof RoleFormData]) {
      setErrors((prev: any) => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePermissionToggle = (permissionId: number) => {
    setFormData(prev => {
      const currentPermissions = Array.isArray(prev.permissions) ? [...prev.permissions] : [];
      const index = currentPermissions.findIndex(id => id === permissionId);
      if (index > -1) {
        currentPermissions.splice(index, 1);
      } else {
        currentPermissions.push(permissionId);
      }
      return { ...prev, permissions: currentPermissions };
    });
  };

  const handleGroupToggle = (resource: string) => {
    const groupPerms = orderedPermissions[resource]?.map(p => p.permission_id) || [];
    setFormData(prev => {
      const currentPermissions = Array.isArray(prev.permissions) ? [...prev.permissions] : [];
      const allSelected = groupPerms.every(id => currentPermissions.includes(id));
      let newPermissions;
      if (allSelected) {
        newPermissions = currentPermissions.filter(id => !groupPerms.includes(id));
      } else {
        const missing = groupPerms.filter(id => !currentPermissions.includes(id));
        newPermissions = [...currentPermissions, ...missing];
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  const toggleGroupCollapse = (resource: string) => {
    setCollapsedGroups(prev => ({ ...prev, [resource]: !prev[resource] }));
  };

  const isGroupSelected = (resource: string): boolean => {
    const groupPerms = orderedPermissions[resource]?.map(p => p.permission_id) || [];
    return groupPerms.length > 0 && groupPerms.every(id => formData.permissions.includes(id));
  };

  const isGroupPartiallySelected = (resource: string): boolean => {
    const groupPerms = orderedPermissions[resource]?.map(p => p.permission_id) || [];
    return groupPerms.some(id => formData.permissions.includes(id)) && !isGroupSelected(resource);
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    if (!formData.name.trim()) newErrors.name = 'Role name is required';
    if (!Array.isArray(formData.permissions) || formData.permissions.length === 0) {
      newErrors.permissions = 'At least one permission must be selected';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const cleanPermissions = Array.isArray(formData.permissions)
        ? formData.permissions.filter(id => typeof id === 'number' && id !== null && id !== undefined)
        : [];
      onSubmit({ name: formData.name.trim(), description: formData.description.trim(), permissions: cleanPermissions });
    }
  };

  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedGroups(items => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handlePermissionDragEnd = (event: DragEndEvent, resource: string) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedPermissions(prev => {
        const items = [...(prev[resource] || [])];
        const oldIndex = items.findIndex(p => p.permission_id.toString() === active.id);
        const newIndex = items.findIndex(p => p.permission_id.toString() === over.id);
        return { ...prev, [resource]: arrayMove(items, oldIndex, newIndex) };
      });
    }
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const groups = orderedGroups.map((resource, groupIndex) => ({
        resource,
        groupOrder: groupIndex,
        permissions: (orderedPermissions[resource] || []).map((perm, permIndex) => ({
          id: perm.permission_id,
          displayOrder: permIndex,
        })),
      }));
      await axiosInstance.put('/roles/permissions/reorder', { groups });
      toast.success('Permission order saved successfully');
    } catch (error) {
      toast.error('Failed to save permission order');
    } finally {
      setSavingOrder(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        onClick={requestClose}
      />

      <div
        className={`fixed right-0 top-0 h-full w-full max-w-3xl bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Add New Role</h2>
              <p className="text-sm text-gray-600 mt-1">Create a new role with specific permissions for your users.</p>
            </div>
            <button onClick={requestClose} className="p-2 hover:bg-gray-200 rounded transition-colors" disabled={loading}>
              <FaTimes className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <form onSubmit={handleSubmit} className="flex flex-col min-h-full">
              <div className="flex-1 px-4 pt-1 pb-4">
                <div className="max-w-3xl mx-auto space-y-4">

                  <div className="bg-white rounded border border-gray-200 overflow-hidden shadow-sm">
                    <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                      <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                        Basic Information
                      </h3>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="space-y-1.5">
                        <label htmlFor="name" className="block text-xs font-medium text-gray-700">
                          Role Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-gray-600 focus:ring-gray-600/20'} rounded focus:ring-2 transition-all duration-200 text-xs placeholder-gray-400`}
                          placeholder="e.g., Content Manager, System Administrator"
                        />
                        {errors.name && (
                          <p className="text-xs text-red-600 flex items-center gap-1">
                            <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <label htmlFor="description" className="block text-xs font-medium text-gray-700">Description</label>
                        <textarea
                          id="description"
                          name="description"
                          rows={3}
                          value={formData.description}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-200 rounded focus:border-gray-600 focus:ring-2 focus:ring-gray-600/20 transition-all duration-200 text-xs placeholder-gray-400 resize-none"
                          placeholder="Describe the role's responsibilities and scope..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded border border-gray-200 overflow-hidden shadow-sm">
                    <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-gray-600 rounded-full"></div>
                          Permissions <span className="text-red-500">*</span>
                        </h3>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleSaveOrder}
                            disabled={savingOrder}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-all duration-200 disabled:opacity-50"
                          >
                            {savingOrder ? (
                              <div className="w-3 h-3 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FaSave className="w-3 h-3" />
                            )}
                            Save Order
                          </button>
                          <span className="text-xs text-gray-500">{formData.permissions.length} selected</span>
                          <div className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                            {orderedGroups.length} groups
                          </div>
                        </div>
                      </div>
                      {errors.permissions && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                          {errors.permissions}
                        </p>
                      )}
                    </div>

                    <div className="p-4">
                      <p className="text-xs text-gray-500 mb-3">Drag groups or permissions to reorder them.</p>
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
                        <SortableContext items={orderedGroups} strategy={verticalListSortingStrategy}>
                          <div className="space-y-3">
                            {orderedGroups.map((resource) => {
                              const resourcePermissions = orderedPermissions[resource] || [];
                              return (
                                <SortableGroup key={resource} id={resource}>
                                  <GroupContent
                                    resource={resource}
                                    resourcePermissions={resourcePermissions}
                                    collapsedGroups={collapsedGroups}
                                    toggleGroupCollapse={toggleGroupCollapse}
                                    handleGroupToggle={handleGroupToggle}
                                    isGroupSelected={isGroupSelected}
                                    isGroupPartiallySelected={isGroupPartiallySelected}
                                    formData={formData}
                                    handlePermissionToggle={handlePermissionToggle}
                                    sensors={sensors}
                                    onPermissionDragEnd={handlePermissionDragEnd}
                                  />
                                </SortableGroup>
                              );
                            })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={requestClose}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <FaPlus className="w-4 h-4" />
                        Create Role
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={() => { setShowCloseModal(false); onClose(); }}
      />
    </>
  );
};

// Group Content Component
const GroupContent: React.FC<{
  resource: string;
  resourcePermissions: Permission[];
  collapsedGroups: Record<string, boolean>;
  toggleGroupCollapse: (resource: string) => void;
  handleGroupToggle: (resource: string) => void;
  isGroupSelected: (resource: string) => boolean;
  isGroupPartiallySelected: (resource: string) => boolean;
  formData: RoleFormData;
  handlePermissionToggle: (permissionId: number) => void;
  sensors: any;
  onPermissionDragEnd: (event: DragEndEvent, resource: string) => void;
  dragHandleProps?: any;
}> = ({
  resource,
  resourcePermissions,
  collapsedGroups,
  toggleGroupCollapse,
  handleGroupToggle,
  isGroupSelected,
  isGroupPartiallySelected,
  formData,
  handlePermissionToggle,
  sensors,
  onPermissionDragEnd,
  dragHandleProps,
}) => {
  const permissionIds = useMemo(
    () => resourcePermissions.map(p => p.permission_id.toString()),
    [resourcePermissions]
  );
  const selectedPermissionCount = useMemo(
    () => resourcePermissions.filter((permission) => formData.permissions.includes(permission.permission_id)).length,
    [resourcePermissions, formData.permissions]
  );

  return (
    <div className="bg-gray-50 rounded border border-gray-100 overflow-hidden">
      <div className="bg-white px-3 py-2.5 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              {...dragHandleProps}
              className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <FaGripVertical className="w-3.5 h-3.5 text-gray-400" />
            </div>
            <button
              type="button"
              onClick={() => toggleGroupCollapse(resource)}
              className="flex items-center gap-2 text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors group"
            >
              <div className="w-5 h-5 bg-white rounded border border-gray-200 flex items-center justify-center group-hover:border-gray-300 transition-colors">
                {collapsedGroups[resource] ? (
                  <FaChevronRight className="w-2.5 h-2.5 text-gray-600" />
                ) : (
                  <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                )}
              </div>
              <span className="capitalize text-sm">{formatDisplayText(resource)}</span>
              <div className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded text-xs font-medium">
                {resourcePermissions.length}
              </div>
              <div className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                {selectedPermissionCount} selected
              </div>
            </button>
          </div>

          <button
            type="button"
            onClick={() => handleGroupToggle(resource)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all duration-200 ${
              isGroupSelected(resource)
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : isGroupPartiallySelected(resource)
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {isGroupSelected(resource) ? (
              <><FaMinus className="w-2.5 h-2.5" /> Deselect All</>
            ) : isGroupPartiallySelected(resource) ? (
              <><FaCheck className="w-2.5 h-2.5" /> Select All</>
            ) : (
              <><FaPlus className="w-2.5 h-2.5" /> Select All</>
            )}
          </button>
        </div>
      </div>

      {!collapsedGroups[resource] && (
        <div className="p-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => onPermissionDragEnd(event, resource)}
          >
            <SortableContext items={permissionIds} strategy={verticalListSortingStrategy}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                {resourcePermissions.map((permission) => (
                  <SortablePermissionItem
                    key={permission.permission_id}
                    id={permission.permission_id.toString()}
                    permission={permission}
                    isSelected={formData.permissions.includes(permission.permission_id)}
                    onToggle={() => handlePermissionToggle(permission.permission_id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};
